import { useSyncExternalStore } from 'react'
import { sdk } from '@/lib/sdk'
import type { GalleryImage } from '@/types/gallery'
import type { MediaUpload } from '@/types/courses'
import { UploadCanceled, putToStorage } from '@/components/features/courses/mediaUpload'
import { readApiErrors } from '@/components/features/courses/courseUtils'
import { watchMediaProcessing } from '@/components/features/media/mediaProcessingWatcher'
import { downscaleImage } from './imageDownscale'
import { announcePlanLimit, isStorageLimitError } from '@/lib/planLimitNotice'

/**
 * Bulk gallery upload that outlives the dialog: the queue lives at module level, so the dialog
 * can be minimized or closed (a floating pill takes over) and the page can change while files
 * keep going. Every file is independent, so one failure never stops the others:
 *
 *   waiting → optimizing (downscaled in a worker, one at a time)
 *           → sending % (signed links asked in batches, PUT straight to storage, 3 at a time)
 *           → processing (confirmed in batches, image created, server job until `ready`)
 *           → done | failed (with the reason and a retry that resumes from the step that failed)
 */

export type GalleryUploadStatus = 'waiting' | 'optimizing' | 'sending' | 'processing' | 'done' | 'failed'

type Ticket = Pick<MediaUpload, 'uploadUrl' | 'method' | 'headers' | 'key'> & { expiresAt?: string }

export interface GalleryUploadItem {
  id: string
  spaceId: string
  file: File
  /** The optimized copy, once made; a retry does not optimize again. */
  upload?: File
  title: string
  description: string
  tags: string[]
  status: GalleryUploadStatus
  percent: number
  ticket?: Ticket
  /** The bytes are in storage under `ticket.key`; a retry only confirms. */
  stored?: boolean
  /** Confirmed; a retry only creates the image. */
  mediaId?: string
  /** The image exists; what is left is the server's processing. */
  imageId?: string
  /** `canceled`, `rateLimited`, `processing`, or the server's message (empty when it sent none). */
  error?: { kind: 'canceled' | 'api' | 'rateLimited' | 'processing'; message?: string | null }
}

export interface GalleryUploadState {
  items: GalleryUploadItem[]
  /** The dialog shows the queue itself; the pill only appears while it is closed. */
  dialogOpen: boolean
}

const CONCURRENCY = 3
/**
 * Each `upload-urls` call is one of only 20 batches an hour per profile, so links are asked for many
 * files at once: when this many are optimized, when optimizing is over, or after a short pause with
 * nothing being sent. Links last 10 minutes, which 25 files at 3 at a time fit comfortably.
 */
const TICKET_BATCH = 25
const TICKET_WAIT_MS = 2_500
const TICKET_MARGIN_MS = 60_000
/** Confirmations also cost one of 20 batches an hour: wait for many files or a short pause. */
const CONFIRM_BATCH = 25
const CONFIRM_WAIT_MS = 4_000

let state: GalleryUploadState = { items: [], dialogOpen: false }
const listeners = new Set<() => void>()
const createdListeners = new Set<(image: GalleryImage) => void>()
const controllers = new Map<string, AbortController>()
let seq = 0

const emit = () => listeners.forEach((listener) => listener())

const setState = (next: Partial<GalleryUploadState>) => {
  state = { ...state, ...next }
  syncLeaveGuard()
  emit()
}

const find = (id: string) => state.items.find((item) => item.id === id)

const patch = (id: string, changes: Partial<GalleryUploadItem>) => {
  if (!find(id)) return
  setState({ items: state.items.map((item) => (item.id === id ? { ...item, ...changes } : item)) })
}

const isActive = (item: GalleryUploadItem) => item.status !== 'done' && item.status !== 'failed'

/** Warns before the tab closes while bytes are still on their way (server processing goes on without the tab). */
const onBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault()
  event.returnValue = ''
}
let guarding = false
const syncLeaveGuard = () => {
  const busy = state.items.some((item) => isActive(item) && !item.imageId)
  if (busy === guarding || typeof window === 'undefined') return
  guarding = busy
  if (busy) window.addEventListener('beforeunload', onBeforeUnload)
  else window.removeEventListener('beforeunload', onBeforeUnload)
}

const failure = (err: unknown): GalleryUploadItem['error'] => {
  if (err instanceof UploadCanceled) return { kind: 'canceled' }
  const status = (err as { response?: { status?: number } })?.response?.status
  const message = readApiErrors(err).message
  if (status === 429) return { kind: 'rateLimited', message }
  return { kind: 'api', message }
}

const fileOf = (item: GalleryUploadItem) => item.upload ?? item.file

/* ---------- optimizing: one at a time, ahead of the upload slots ---------- */

let optimizing = false
const optimizeNext = async () => {
  if (optimizing) return
  const next = state.items.find((item) => item.status === 'waiting' && !item.upload && !item.stored && !item.mediaId)
  if (!next) return
  optimizing = true
  patch(next.id, { status: 'optimizing' })
  const upload = await downscaleImage(next.file)
  optimizing = false
  const current = find(next.id)
  // Canceled or removed meanwhile: keep the copy for a retry, do not resurrect it.
  if (current) patch(next.id, { upload, status: current.status === 'optimizing' ? 'waiting' : current.status })
  pump()
}

/* ---------- signed links, in batches ---------- */

let ticketRequest: Promise<void> | null = null
let ticketTimer: ReturnType<typeof setTimeout> | undefined
const ticketValid = (item: GalleryUploadItem) =>
  !!item.ticket && (!item.ticket.expiresAt || Date.parse(item.ticket.expiresAt) - Date.now() > TICKET_MARGIN_MS)
const needsTicket = (item: GalleryUploadItem) =>
  item.status === 'waiting' && !!item.upload && !item.stored && !item.mediaId && !ticketValid(item)
const stillOptimizing = () =>
  state.items.some((item) => (item.status === 'waiting' || item.status === 'optimizing') && !item.upload && !item.stored && !item.mediaId)

const requestTickets = () => {
  if (ticketTimer) { clearTimeout(ticketTimer); ticketTimer = undefined }
  if (ticketRequest) return
  const ready = state.items.filter(needsTicket)
  if (ready.length === 0) return
  const spaceId = ready[0].spaceId
  const batch = ready.filter((item) => item.spaceId === spaceId).slice(0, TICKET_BATCH)
  ticketRequest = (async () => {
    try {
      const { items } = await sdk.media.requestUploads({
        purpose: 'galleryImage', spaceId,
        files: batch.map((item) => ({ name: fileOf(item).name, mimeType: fileOf(item).type, size: fileOf(item).size })),
      })
      for (const answer of items || []) {
        const item = batch[answer.index]
        if (!item) continue
        if (answer.error) patch(item.id, { status: 'failed', error: { kind: 'api', message: answer.error.message } })
        else patch(item.id, { ticket: { uploadUrl: answer.uploadUrl, method: answer.method, headers: answer.headers, key: answer.key, expiresAt: answer.expiresAt } })
      }
    } catch (err) {
      // The whole batch was refused (rate limit, pending cap, the plan's storage): every file says why.
      if (isStorageLimitError(err)) announcePlanLimit({ kind: 'storage' })
      const error = failure(err)
      for (const item of batch) if (find(item.id)?.status === 'waiting') patch(item.id, { status: 'failed', error })
    }
  })()
  void ticketRequest.finally(() => {
    ticketRequest = null
    pump()
  })
}

/** Asks for links now if a batch is worth it, or soon if nothing else would ask. */
const scheduleTickets = () => {
  if (ticketRequest) return
  const ready = state.items.filter(needsTicket).length
  if (ready === 0) return
  if (ready >= TICKET_BATCH || !stillOptimizing()) { requestTickets(); return }
  const sending = state.items.some((item) => item.status === 'sending')
  // While files are being sent, the next one to finish (or the end of optimizing) asks again.
  if (!sending && !ticketTimer) ticketTimer = setTimeout(requestTickets, TICKET_WAIT_MS)
}

/* ---------- sending: PUT straight to storage ---------- */

const send = async (id: string) => {
  const controller = new AbortController()
  controllers.set(id, controller)
  patch(id, { status: 'sending', percent: 0, error: undefined })
  try {
    const item = find(id)
    if (!item || item.status === 'failed') return
    if (!item.ticket) throw new Error('no upload link')
    if (controller.signal.aborted) throw new UploadCanceled()
    await putToStorage({ ...item.ticket, name: fileOf(item).name, maxBytes: 0 }, fileOf(item), (percent) => {
      const current = find(id)
      if (current && current.percent !== percent && percent < 100) patch(id, { percent })
    }, controller.signal)
    patch(id, { stored: true, status: 'processing', percent: 100 })
    queueConfirm()
  } catch (err) {
    // A link that failed may have expired: the retry asks for a new one.
    patch(id, { status: 'failed', ticket: undefined, error: failure(err) })
  } finally {
    controllers.delete(id)
    pump()
  }
}

/* ---------- confirming and creating, in batches ---------- */

let confirmTimer: ReturnType<typeof setTimeout> | undefined
let confirming = false

const awaitingConfirm = () => state.items.filter((item) => item.status === 'processing' && item.stored && !item.mediaId)
const awaitingCreate = () => state.items.filter((item) => item.status === 'processing' && item.mediaId && !item.imageId)

const queueConfirm = () => {
  const pending = awaitingConfirm().length + awaitingCreate().length
  const stillSending = state.items.some((item) => item.status === 'sending' || (item.status === 'waiting' && !item.mediaId))
  if (pending >= CONFIRM_BATCH || (pending > 0 && !stillSending)) { void flushConfirm(); return }
  if (pending > 0 && !confirmTimer) confirmTimer = setTimeout(() => { void flushConfirm() }, CONFIRM_WAIT_MS)
}

const flushConfirm = async () => {
  if (confirmTimer) { clearTimeout(confirmTimer); confirmTimer = undefined }
  if (confirming) return
  confirming = true
  try {
    const toConfirm = awaitingConfirm().slice(0, 50)
    if (toConfirm.length) {
      const spaceId = toConfirm[0].spaceId
      const group = toConfirm.filter((item) => item.spaceId === spaceId)
      try {
        const { items } = await sdk.media.confirmBatch({
          spaceId, items: group.map((item) => ({ key: item.ticket?.key || '', name: fileOf(item).name, size: fileOf(item).size })),
        })
        for (const answer of items || []) {
          const item = group[answer.index]
          if (!item) continue
          if (answer.asset) patch(item.id, { mediaId: answer.asset.id })
          // A rejected object is gone: the retry uploads again.
          else patch(item.id, { status: 'failed', stored: false, ticket: undefined, error: { kind: 'api', message: answer.error?.message } })
        }
      } catch (err) {
        const error = failure(err)
        for (const item of group) patch(item.id, { status: 'failed', error })
      }
    }

    const toCreate = awaitingCreate().slice(0, 50)
    if (toCreate.length) {
      const spaceId = toCreate[0].spaceId
      const group = toCreate.filter((item) => item.spaceId === spaceId)
      try {
        const created = await sdk.gallery.createFromMedia(spaceId, group.map((item) => ({
          mediaId: item.mediaId as string, title: item.title.trim() || undefined, description: item.description || undefined, tags: item.tags,
        })))
        group.forEach((item, index) => {
          const image = created.find((entry) => entry.mediaId === item.mediaId) ?? created[index]
          if (!image) return
          patch(item.id, { imageId: image.id })
          createdListeners.forEach((listener) => listener(image))
          if (image.processingStatus && image.processingStatus !== 'ready') watchItem(item.id, item.mediaId as string)
          else patch(item.id, { status: 'done' })
        })
      } catch (err) {
        const error = failure(err)
        for (const item of group) patch(item.id, { status: 'failed', error })
      }
    }
  } finally {
    confirming = false
    // More may have arrived meanwhile.
    if (awaitingConfirm().length || awaitingCreate().length) queueConfirm()
  }
}

/* ---------- processing on the server ---------- */

const watchItem = (id: string, mediaId: string) => {
  watchMediaProcessing([mediaId], (_, outcome) => {
    const item = find(id)
    if (!item) return
    if (outcome === 'ready') { patch(id, { status: 'done' }); return }
    // The server could not process it and gave the storage back: drop the empty image and start over on retry.
    if (item.imageId) {
      void sdk.gallery.delete(item.imageId).catch(() => undefined)
      removedListeners.forEach((listener) => listener(item.imageId as string))
    }
    patch(id, { status: 'failed', error: { kind: 'processing' }, ticket: undefined, stored: false, mediaId: undefined, imageId: undefined })
  })
}

/* ---------- scheduling ---------- */

const pump = () => {
  void optimizeNext()
  let busy = state.items.filter((item) => item.status === 'sending').length
  for (const item of state.items) {
    if (busy >= CONCURRENCY) break
    if (item.status !== 'waiting' || controllers.has(item.id)) continue
    if (item.mediaId || item.stored) {
      // Resuming after a failure past the upload: straight back to the batch step.
      patch(item.id, { status: 'processing', error: undefined })
      queueConfirm()
      continue
    }
    // Sent only with a valid link; links are asked for in batches.
    if (!item.upload || !ticketValid(item)) continue
    busy += 1
    void send(item.id)
  }
  scheduleTickets()
}

/* ---------- public API ---------- */

export const enqueueGalleryUploads = (
  spaceId: string,
  entries: Array<{ file: File; title: string; description: string; tags: string[] }>,
) => {
  const added: GalleryUploadItem[] = entries.map((entry) => ({
    ...entry, id: `gu-${Date.now()}-${seq++}`, spaceId, status: 'waiting', percent: 0,
  }))
  setState({ items: [...state.items, ...added] })
  pump()
}

export const retryGalleryUpload = (id?: string) => {
  setState({
    items: state.items.map((item) =>
      item.status === 'failed' && (!id || item.id === id) ? { ...item, status: 'waiting', percent: 0, error: undefined } : item),
  })
  pump()
}

/** Stops what has not been sent yet; those files stay in the list as failed, ready for a retry. */
export const cancelGalleryUploads = () => {
  setState({
    items: state.items.map((item) =>
      item.status === 'waiting' || item.status === 'optimizing' ? { ...item, status: 'failed', error: { kind: 'canceled' } } : item),
  })
  controllers.forEach((controller) => controller.abort())
}

const forget = (item: GalleryUploadItem) => {
  // A confirmed file that never became an image is dropped on the server.
  if (!item.imageId && item.mediaId) void sdk.media.delete(item.mediaId).catch(() => undefined)
}

export const removeGalleryUpload = (id: string) => {
  const item = find(id)
  if (!item || isActive(item)) return
  forget(item)
  setState({ items: state.items.filter((entry) => entry.id !== id) })
}

/** Clears the list once nothing is in flight. */
export const dismissGalleryUploads = () => {
  if (state.items.some(isActive)) return
  state.items.forEach(forget)
  setState({ items: [] })
}

export const setGalleryUploadDialogOpen = (dialogOpen: boolean) => {
  if (state.dialogOpen !== dialogOpen) setState({ dialogOpen })
}

/** Each image as soon as it exists (still processing), so the gallery shows it without waiting for the batch. */
export const onGalleryImageCreated = (listener: (image: GalleryImage) => void) => {
  createdListeners.add(listener)
  return () => { createdListeners.delete(listener) }
}

const removedListeners = new Set<(imageId: string) => void>()
/** An image the queue had to delete because the server could not process it. */
export const onGalleryImageRemoved = (listener: (imageId: string) => void) => {
  removedListeners.add(listener)
  return () => { removedListeners.delete(listener) }
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export interface GalleryUploadSummary {
  total: number
  done: number
  failed: number
  active: number
  /** Files whose bytes reached storage (processing on the server or done). */
  sent: number
  /** Files the server is still processing. */
  processing: number
  /** Still optimizing or sending bytes: the browser tab matters. */
  uploading: boolean
  /** Bytes sent over the bytes of the files that have not failed. */
  percent: number
  finished: boolean
}

export const summarize = (items: GalleryUploadItem[]): GalleryUploadSummary => {
  let bytes = 0
  let sent = 0
  let done = 0
  let failed = 0
  let processing = 0
  let uploading = false
  for (const item of items) {
    if (item.status === 'done') done += 1
    if (item.status === 'processing') processing += 1
    if (item.status === 'waiting' || item.status === 'optimizing' || item.status === 'sending') uploading = true
    if (item.status === 'failed') { failed += 1; continue }
    const size = fileOf(item).size
    bytes += size
    sent += item.status === 'done' || item.status === 'processing' ? size : item.status === 'sending' ? (size * item.percent) / 100 : 0
  }
  const active = items.length - done - failed
  return {
    total: items.length, done, failed, active, sent: done + processing, processing, uploading,
    percent: bytes > 0 ? Math.floor((sent / bytes) * 100) : items.length > 0 ? 100 : 0,
    finished: items.length > 0 && active === 0,
  }
}

export const useGalleryUploads = () => useSyncExternalStore(subscribe, () => state, () => state)
